import { getFullName } from '@zorro/common-utils';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Title } from '@zorro/design-system/v2/Title';
import { singleLineTruncatedTextWithEllipsis } from '@zorro/shared/formatters';
import { MODAL_ZINDEX } from '@zorro/types';
import { Box, Grid, Group, Stack } from '@zorro/web/design-system';
import { CommentsPopover } from '@zorro/web/shared-ui';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { ReactNode } from 'react';

import { EnrollmentAllowanceCard } from '../../EnrollmentAllowanceCard';

type Props = {
  children: ReactNode;
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
};

export const EnrollmentConfirmationStep = ({
  children,
  employerId,
  employeeId,
  onboardingPeriodId,
  benefitId,
}: Props) => {
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const title = employee
    ? `Enrollment confirmation for ${getFullName(employee)}`
    : 'Enrollment confirmation';

  return (
    <Stack mih="calc(100vh - 18.5rem)" pl="xl" pb="xxl" gap={0}>
      <Stack
        style={{ zIndex: MODAL_ZINDEX + 1 }}
        justify="space-between"
        bg="zorroWhite.0"
        pos="sticky"
        pt="md"
        top="0"
      >
        <Group justify="space-between" wrap="nowrap">
          <Box maw="50rem" style={singleLineTruncatedTextWithEllipsis}>
            <Title order={1} display="inline">
              {title}
            </Title>
          </Box>
          <CommentsPopover
            onboardingPeriodId={onboardingPeriodId}
            employerId={employerId}
            employeeId={employeeId}
          />
        </Group>
        <Divider />
      </Stack>
      <Grid overflow="visible" pt="xl">
        <Grid.Col span={{ base: 8, xl: 9 }}>
          <Stack w="100%" gap="xl">
            {children}
          </Stack>
        </Grid.Col>
        <Grid.Col
          span={{ base: 4, xl: 3 }}
          h="min-content"
          pos="sticky"
          top="5rem"
        >
          <Group justify="end">
            <EnrollmentAllowanceCard
              employeeId={employeeId}
              onboardingPeriodId={onboardingPeriodId}
              highlightedBenefitId={benefitId}
              withOnboardingAllowance
            />
          </Group>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
